import React, { useState } from "react";
import {
  Button,
  Checkbox,
  DropdownField,
  FormSection,
  TextField,
  useProblemReport,
  useToast,
  type ProblemReport,
} from "../../../components/ui";
import {
  normaliseEmailInput,
  normalisePhoneInput,
  normalisePincodeInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import UploadField from "./UploadField";
import { BANK_OPTIONS, ifscPrefixForBank } from "../../../lib/banks";
import { STATE_OPTIONS, citiesOfState } from "../../../lib/locations";
import { BrandDetails } from "../types/brand.types";
import {
  BRAND_NAME_MAX,
  BrandErrors,
  ValidatedField,
  normaliseAccountNumberInput,
  normaliseIfscInput,
  normaliseUpiIdInput,
} from "../validation";

/**
 * The validated fields in the order they are laid out, which is the order the
 * first problem has to be looked for in — `VALIDATED_FIELDS` is grouped by
 * required-ness instead, and would send you to the wrong one.
 */
const FIELD_ORDER: ValidatedField[] = [
  "fullName",
  "brandName",
  "phone",
  "whatsapp",
  "email",
  "state",
  "city",
  "pincode",
  "accountHolder",
  "accountNumber",
  "bankName",
  "ifsc",
  "upiNumber",
  "upiId",
];

/** Stable id per control, so a failing field can be reached and focused. */
const controlId = (field: ValidatedField) => `brand-${field}`;

interface BrandDetailsFormProps {
  details: BrandDetails;
  /** Patch merge — every control sends only the key it owns. */
  onChange: (patch: Partial<BrandDetails>) => void;
  /** Failing required fields, keyed by field name. */
  errors: BrandErrors;
  onSave: () => void;
  onCancel: () => void;
  /** Save stays disabled until something differs from the saved copy. */
  dirty: boolean;
  /** True while the save is with the server — both buttons stand down. */
  saving?: boolean;
  /**
   * Whether the brand as stored is already complete, which is what the Save
   * button is named after: setting the brand up the first time, changing it
   * every time after. Read from the saved record rather than the boxes on
   * screen — see the page.
   */
  setUp?: boolean;
  /**
   * Filled with this form's own way of pointing at what is missing, so the
   * page above can ask for it — the rail's unsaved prompt does. See
   * `useProblemReport`.
   */
  problems?: ProblemReport;
}

const BrandDetailsForm: React.FC<BrandDetailsFormProps> = ({
  details,
  onChange,
  errors,
  onSave,
  onCancel,
  dirty,
  saving = false,
  setUp = false,
  problems,
}) => {
  const toast = useToast();

  // A field's error only surfaces once it has been visited, so the form does
  // not open covered in red before anything has been typed.
  const [touched, setTouched] = useState<
    Partial<Record<ValidatedField, boolean>>
  >({});

  const visit = (field: ValidatedField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  const errorFor = (field: ValidatedField) =>
    touched[field] ? errors[field] : undefined;

  const valid = Object.keys(errors).length === 0;

  // While the numbers are linked, editing the phone has to carry the WhatsApp
  // number with it — otherwise the two drift apart behind a ticked box.
  const handlePhoneChange = (raw: string) => {
    const phone = normalisePhoneInput(raw);
    onChange(details.whatsappSameAsPhone ? { phone, whatsapp: phone } : { phone });
  };

  // The city list belongs to the state, so changing the state drops whatever
  // city was picked under the old one — keeping it would leave the address
  // naming a city that is not in the state beneath it.
  const handleStateChange = (state: string) => onChange({ state, city: "" });

  /**
   * Naming the bank settles the first half of the IFSC — four letters and the
   * reserved `0` — so the form fills it in and leaves only the branch code.
   *
   * Changing the bank clears the field back to that prefix, branch code and
   * all. A branch code belongs to one branch of one bank, so under a different
   * bank's letters it is not a code that needs correcting — it is six
   * characters that were never about this bank. Carrying them over would leave
   * a full, well-formed IFSC on screen that quietly pays somewhere else, and a
   * code that looks finished is a code nobody re-reads.
   */
  const handleBankNameChange = (bankName: string) => {
    const prefix = ifscPrefixForBank(bankName);
    // A bank the list does not hold settles nothing, so the code is left alone
    // and typed in full.
    onChange(prefix ? { bankName, ifsc: `${prefix}0` } : { bankName });
  };

  // Unticking leaves the copied number in place rather than blanking it: it is
  // almost always the right starting point for the edit that follows.
  const handleSameAsPhone = (checked: boolean) =>
    onChange(
      checked
        ? { whatsappSameAsPhone: true, whatsapp: details.phone }
        : { whatsappSameAsPhone: false }
    );

  const handleCancel = () => {
    setTouched({});
    onCancel();
  };

  /**
   * Points at the problem rather than describing it: every failing field
   * reports itself at once, and the page moves to the first of them and puts
   * the cursor in it.
   */
  const reportProblems = () => {
    const firstBad = FIELD_ORDER.find((field) => errors[field]);
    if (!firstBad) return;

    setTouched(
      Object.fromEntries(FIELD_ORDER.map((field) => [field, true]))
    );

    // Focus without its own jump, so the smooth scroll is the only movement
    // and the field lands in the middle of the column rather than its edge.
    const control = document.getElementById(controlId(firstBad));
    control?.focus({ preventScroll: true });
    control?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  // Also reachable from the page above, for the one press that is not made on
  // this form: the rail's unsaved prompt offering to save it.
  useProblemReport(problems, reportProblems);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A save already with the server: pressing Enter in a field must not send
    // a second pair of PATCHes after it.
    if (saving) return;

    // Enter in a field reaches this even while the button is off, so the
    // incomplete form is marked up here too rather than silently ignored.
    if (!valid) {
      reportProblems();
      return;
    }

    onSave();
  };

  return (
    <form
      className="flex flex-col lg:h-full lg:min-h-0"
      onSubmit={handleSubmit}
    >
      {/* Only the sections scroll — the actions below stay put, so Save is
          reachable from anywhere in a form this long. `overflow` is set at
          every width, but it only bites from `lg`, where the column is height
          bound; below that the box grows and the page scrolls as before. */}
      <div className="flex flex-col gap-5 pb-5 lg:min-h-0 lg:flex-1 lg:pr-1 custom-scrollbar-overlay">
        <FormSection title="Basic details">
          <TextField
            label="Display Name"
            id={controlId("fullName")}
            placeholder="eg. Riya Roy"
            autoComplete="name"
            required
            value={details.fullName}
            error={errorFor("fullName")}
            onBlur={visit("fullName")}
            // Cased on the way in rather than on save, so what the invoice
            // preview shows is exactly what the field holds. Every word is
            // capitalised, which is what makes one field enough here.
            onValueChange={(fullName) =>
              onChange({ fullName: toNameCase(fullName) })
            }
          />
        </FormSection>

        <FormSection title="Brand info">
          <div className="flex flex-col gap-4">
            <TextField
              label="Brand name"
              id={controlId("brandName")}
              placeholder="eg. Nutro diet studio"
              required
              // Stopped at the ceiling rather than allowed past it and marked
              // up after: the name is a heading on every document the brand
              // sends out, and there is nothing to be gained from typing one
              // that will not fit.
              maxLength={BRAND_NAME_MAX}
              // Counted rather than just stated, so the field that has quietly
              // stopped accepting letters has already said why.
              hint={`${details.brandName.length}/${BRAND_NAME_MAX} characters`}
              value={details.brandName}
              error={errorFor("brandName")}
              onBlur={visit("brandName")}
              onValueChange={(brandName) => onChange({ brandName })}
            />
            <UploadField
              label="Upload logo icon"
              placeholder="Click to upload logo"
              hint="Maximum 1MB and 4096 × 4096 px. Only PNG, JPEG & WebP format allowed"
              purpose="BRAND_LOGO"
              crop
              value={details.logo}
              onValueChange={(logo) => onChange({ logo })}
            />
          </div>
        </FormSection>

        <FormSection title="Your communication details">
          <div className="flex flex-col gap-4">
            

            <TextField
              label="Full address"
              placeholder="1202, Galaxy apartment, SV Road"
              value={details.address}
              onValueChange={(address) => onChange({ address })}
            />

            {/* Three across only from `xl` — at `lg` the preview takes enough of
                the row that three fields would be too narrow to read. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {/* State leads City, because it is what fills it: laid out the
                  other way round the reader meets a dead control before the
                  one that brings it to life. Both lists are typed into rather
                  than scrolled — 34 states and up to 120 cities are more than
                  anyone wants to hunt through. */}
              <DropdownField
                label="State"
                id={controlId("state")}
                placeholder="eg. Maharashtra"
                options={STATE_OPTIONS}
                searchable
                required
                value={details.state}
                error={errorFor("state")}
                onBlur={visit("state")}
                onValueChange={handleStateChange}
              />
              <DropdownField
                label="City"
                id={controlId("city")}
                placeholder={
                  details.state ? "eg. Mumbai" : "Select a state first"
                }
                options={citiesOfState(details.state)}
                disabled={!details.state}
                // Reaching for the city before there is a state to fill it is a
                // reasonable thing to do, so it gets an answer rather than a
                // control that simply refuses to respond.
                onDisabledClick={() =>
                  toast.warning(
                    "Select a state first",
                    "The city list is filled by the state you pick."
                  )
                }
                searchable
                // The list covers the common cases and cannot claim to be
                // exhaustive: a town it has never heard of is still where
                // somebody's kitchen is. Typing one offers it as a "Use …" row
                // under whatever matched, and a value already saved that way
                // shows in the closed control rather than reading as blank.
                creatable
                required
                value={details.city}
                error={errorFor("city")}
                onBlur={visit("city")}
                onValueChange={(city) => onChange({ city })}
              />
              <TextField
                label="Pincode"
                id={controlId("pincode")}
                placeholder="eg. 410253"
                inputMode="numeric"
                maxLength={6}
                required
                value={details.pincode}
                error={errorFor("pincode")}
                onBlur={visit("pincode")}
                onValueChange={(pincode) =>
                  onChange({ pincode: normalisePincodeInput(pincode) })
                }
              />
            </div>

            {/* Three across only from `xl` — at `lg` the preview takes enough of
                the row that three fields would be too narrow to read. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              <TextField
                label="Phone number"
                id={controlId("phone")}
                placeholder=" 7869369854"
                // The field holds the ten subscriber digits; the dialling code
                // is drawn beside them and put back on for the API.
                prefix="+91"
                inputMode="numeric"
                autoComplete="tel"
                // The normaliser already stops at ten digits; `maxLength` is
                // what makes the browser say so before the keystroke lands.
                maxLength={10}
                required
                value={details.phone}
                error={errorFor("phone")}
                onBlur={visit("phone")}
                onValueChange={handlePhoneChange}
              />
              <div>
                <TextField
                  label="WatsApp number"
                  id={controlId("whatsapp")}
                  placeholder=" 7869369854"
                  prefix="+91"
                  inputMode="numeric"
                  maxLength={10}
                  // Mirrored fields are read-only: the phone above is the one
                  // place the number is edited while the box is ticked.
                  disabled={details.whatsappSameAsPhone}
                  value={details.whatsapp}
                  // Optional, so it only reports itself once it holds a part
                  // number — never for being left empty.
                  error={errorFor("whatsapp")}
                  onBlur={visit("whatsapp")}
                  onValueChange={(whatsapp) =>
                    onChange({ whatsapp: normalisePhoneInput(whatsapp) })
                  }
                />
                <Checkbox
                  label="Same as phone number"
                  className="mt-2"
                  checked={details.whatsappSameAsPhone}
                  onCheckedChange={handleSameAsPhone}
                />
              </div>
              <TextField
                label="Email address"
                id={controlId("email")}
                type="email"
                placeholder="eg. riyaroy@gmail.com"
                autoComplete="email"
                required
                value={details.email}
                error={errorFor("email")}
                onBlur={visit("email")}
                onValueChange={(email) =>
                  onChange({ email: normaliseEmailInput(email) })
                }
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Payments details">
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="mb-1.5 text-[13px] 3xl:text-[14px] text-ink">
                Bank details
              </h3>
              {/* The bank rows are unlabelled in the design, so each placeholder
                  doubles as the field's accessible name — and carries the
                  asterisk on the ones that are required. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <TextField
                  placeholder="Account holder name *"
                  aria-label="Account holder name"
                  id={controlId("accountHolder")}
                  value={details.accountHolder}
                  error={errorFor("accountHolder")}
                  onBlur={visit("accountHolder")}
                  onValueChange={(accountHolder) => onChange({ accountHolder })}
                />
                <TextField
                  placeholder="Account number *"
                  aria-label="Account number"
                  id={controlId("accountNumber")}
                  inputMode="numeric"
                  maxLength={18}
                  value={details.accountNumber}
                  error={errorFor("accountNumber")}
                  onBlur={visit("accountNumber")}
                  onValueChange={(accountNumber) =>
                    onChange({
                      accountNumber: normaliseAccountNumberInput(accountNumber),
                    })
                  }
                />
                {/* Picked from the RBI's list so the IFSC prefix below can be
                    filled in from it — but `creatable`, because that list is
                    long rather than complete, and a bank missing from it is
                    not a reason to be unable to be paid. */}
                <DropdownField
                  placeholder="Bank name *"
                  aria-label="Bank name"
                  id={controlId("bankName")}
                  options={BANK_OPTIONS}
                  searchable
                  creatable
                  value={details.bankName}
                  error={errorFor("bankName")}
                  onBlur={visit("bankName")}
                  onValueChange={handleBankNameChange}
                />
                {/* Optional — the bank name above is what the payout is filed
                    under, and the code can follow later. Still checked once
                    anything is in it: half a code is worse than none. */}
                <TextField
                  placeholder="IFSC Code"
                  aria-label="IFSC Code"
                  id={controlId("ifsc")}
                  maxLength={11}
                  value={details.ifsc}
                  error={errorFor("ifsc")}
                  onBlur={visit("ifsc")}
                  onValueChange={(ifsc) =>
                    onChange({ ifsc: normaliseIfscInput(ifsc) })
                  }
                />
                {/* Both UPI rows are optional — they only report themselves
                    once there is a part-finished address in them. */}
                <TextField
                  placeholder="UPI number"
                  id={controlId("upiNumber")}
                  inputMode="numeric"
                  maxLength={10}
                  value={details.upiNumber}
                  error={errorFor("upiNumber")}
                  onBlur={visit("upiNumber")}
                  onValueChange={(upiNumber) =>
                    onChange({ upiNumber: normalisePhoneInput(upiNumber) })
                  }
                />
                <TextField
                  placeholder="UPI ID"
                  id={controlId("upiId")}
                  value={details.upiId}
                  error={errorFor("upiId")}
                  onBlur={visit("upiId")}
                  onValueChange={(upiId) =>
                    onChange({ upiId: normaliseUpiIdInput(upiId) })
                  }
                />
              </div>
            </div>

            <UploadField
              label="Upload your payment QR"
              placeholder="Click to upload payment QR code"
              // The same rules as the logo plus one of its own, so they are
              // stated here too — a field that only mentions its limit once it
              // has been broken is a field that gets broken. The QR rule leads,
              // because it is the one that is not guessable from the picker.
              hint="Must be a QR code image. Maximum 1MB and 4096 × 4096 px. Only PNG, JPEG & WebP format allowed"
              purpose="PAYMENT_QR"
              requireQrCode
              value={details.paymentQr}
              onValueChange={(paymentQr) => onChange({ paymentQr })}
            />
          </div>
        </FormSection>
      </div>

      {/* Pinned under the scrolling sections, and carded like them so it reads
          as part of the same form rather than page furniture. `sticky` is what
          holds it to the foot of the viewport below `lg`, where the whole page
          scrolls and the bar is an ordinary last child; from `lg` up the flex
          row above has already taken the height and it simply sits there.

          The dividing rule belongs to this block rather than the scrolling one
          — riding at the top of the bar it stays on the seam, where a rule at
          the foot of the sections would scroll away with them. Both fills are
          load-bearing: the page tint covers the gap under the rule, the white
          covers the card as the sections pass beneath it. */}
      <div className="sticky bottom-0 z-10 flex-shrink-0 border-t border-line bg-[#FCFCFD] pt-4">
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-white p-3">
          {/* Off until every required field is filled — the form says whether
              it can be saved before it is pressed rather than after. While a
              required field is missing it is off the soft way (`onDisabledClick`
              rather than the native attribute): it greys out and reads as
              disabled, but a press still marks up what is missing, so the
              button that cannot be used is still the thing that says why.
              Before the first edit and mid-save there is nothing to explain, so
              it is off the plain way. `type` follows: submitting is the valid
              form's job, and while it is invalid the press is the reporter's.
              `aria-invalid` is what tells a screen reader why it is off. */}
          <Button
            type={valid ? "submit" : "button"}
            size="md"
            disabled={!dirty || !valid || saving}
            onDisabledClick={
              dirty && !valid && !saving ? reportProblems : undefined
            }
            aria-invalid={dirty && !valid ? true : undefined}
            className="min-w-[130px]"
          >
            {/* A brand that is already complete is being changed rather than
                set up, and the button says which — the first save is the one
                that names the job it finishes. */}
            {saving ? "Saving…" : setUp ? "Save changes" : "Setup brand"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={handleCancel}
            // Cancelling mid-save would restore values the server is at that
            // moment replacing, so it waits for the answer like everything else.
            disabled={!dirty || saving}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BrandDetailsForm;
